import React from 'react';
import MarketConnectionPanel from './MarketConnectionPanel';
import { AlertTriangle, RefreshCw, FolderOpen, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatDateTimeDisplay } from '../../lib/datetime';

const OperationsTab = ({
  executionOverview,
  executionJobs,
  retryingExecutionJobId,
  marketDataJobs,
  marketDataCoverage,
  marketDataSymbolOptions,
  marketRefreshForm,
  setMarketRefreshForm,
  runningMarketRefresh,
  brokerGapRepairForm,
  brokerGapRepairStartInputRef,
  brokerGapRepairEndInputRef,
  brokerGapRepairPreview,
  brokerGapRepairConfirmation,
  setBrokerGapRepairConfirmation,
  previewingBrokerGapRepair,
  applyingBrokerGapRepair,
  appTimeZone,
  fetchData,
  handleRetryExecutionJob,
  deadLetterCleanupPreview,
  deadLetterCleanupOlderThanHours,
  setDeadLetterCleanupOlderThanHours,
  deadLetterCleanupIncludeClose,
  setDeadLetterCleanupIncludeClose,
  deadLetterCleanupConfirmation,
  setDeadLetterCleanupConfirmation,
  deadLetterCleanupPreviewing,
  deadLetterCleanupApplying,
  deadLetterCleanupConfirmationPhrase,
  handlePreviewDeadLetterCleanup,
  handleApplyDeadLetterCleanup,
  handleRunMarketRefresh,
  handleBrokerGapRepairField,
  handlePreviewBrokerGapRepair,
  handleApplyBrokerGapRepair,
  handleMarketRefreshSymbolChange,
  openMarketBrowser,
}) => {
  const cleanupCounts = deadLetterCleanupPreview?.counts || {};
  const cleanupExclusions = deadLetterCleanupPreview?.exclusions || {};
  const cleanupTotal = cleanupCounts.total_eligible || 0;
  const canApplyCleanup = Boolean(deadLetterCleanupPreview)
    && cleanupTotal > 0
    && deadLetterCleanupConfirmation.trim() === deadLetterCleanupConfirmationPhrase
    && !deadLetterCleanupPreviewing
    && !deadLetterCleanupApplying;

  return (
    <div className="space-y-6">
      <MarketConnectionPanel />
      <div className="grid grid-cols-1 lg:grid-cols-[0.95fr,1.05fr] gap-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Execution Queue Health</CardTitle>
                <CardDescription>Platform-wide queue state and live broker-sync health.</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="text-xs text-zinc-500">Queued</div>
              <div className="mt-1 text-2xl font-semibold">{executionOverview.jobs?.queued || 0}</div>
            </div>
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="text-xs text-zinc-500">Retrying</div>
              <div className="mt-1 text-2xl font-semibold text-yellow-400">{executionOverview.jobs?.retrying || 0}</div>
            </div>
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="text-xs text-zinc-500">Dead Letters</div>
              <div className="mt-1 text-2xl font-semibold text-red-400">{executionOverview.jobs?.dead_letter || 0}</div>
            </div>
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="text-xs text-zinc-500">Live Trades</div>
              <div className="mt-1 text-2xl font-semibold">{executionOverview.live_trades?.total || 0}</div>
            </div>
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="text-xs text-zinc-500">Stale Sync</div>
              <div className="mt-1 text-2xl font-semibold text-yellow-400">{executionOverview.live_trades?.stale_sync || 0}</div>
            </div>
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="text-xs text-zinc-500">Anomalies</div>
              <div className="mt-1 text-2xl font-semibold text-orange-400">{executionOverview.live_trades?.anomalies || 0}</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle>Recent Dead Letters</CardTitle>
            <CardDescription>Jobs that exhausted retries and need review.</CardDescription>
          </CardHeader>
          <CardContent>
            {executionOverview.dead_letters?.length === 0 ? (
              <div className="text-sm text-zinc-500">No dead letters right now.</div>
            ) : (
              <div className="space-y-3">
                {executionOverview.dead_letters.map((job) => (
                  <div key={job.id} className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium text-white">
                          {job.job_type} {job.broker_name ? `• ${job.broker_name}` : ""}
                        </div>
                        <div className="text-xs text-zinc-500">
                          User {job.user_id?.slice(0, 8)} • Trade {job.trade_id?.slice(0, 8) || "n/a"}
                        </div>
                        <div className="mt-1 text-xs text-red-300">{job.last_error || "No error detail"}</div>
                      </div>
                      <Badge variant="destructive">
                        {job.dead_lettered_at ? formatDateTimeDisplay(job.dead_lettered_at, appTimeZone) : "Dead letter"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>ProjectX Broker Gap Repair</CardTitle>
          <CardDescription>
            Preview an exact one-minute historical window, then queue a governed broker backfill and aggregate rebuild.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="text-xs text-zinc-500">Symbol</label>
              <Select
                value={brokerGapRepairForm.symbol}
                onValueChange={(value) => handleBrokerGapRepairField('symbol', value)}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {marketDataSymbolOptions.map((option) => (
                    <SelectItem key={`gap-${option.value}`} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-zinc-500">Start (browser local time)</label>
              <Input
                ref={brokerGapRepairStartInputRef}
                type="datetime-local"
                value={brokerGapRepairForm.start_time}
                onChange={(event) => handleBrokerGapRepairField('start_time', event.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500">End (browser local time)</label>
              <Input
                ref={brokerGapRepairEndInputRef}
                type="datetime-local"
                value={brokerGapRepairForm.end_time}
                onChange={(event) => handleBrokerGapRepairField('end_time', event.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500">Maximum one-minute bars</label>
              <Input
                type="number"
                min="60"
                max="30000"
                step="1"
                value={brokerGapRepairForm.maximum_bars}
                onChange={(event) => handleBrokerGapRepairField('maximum_bars', event.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          <div className="flex items-start gap-2 rounded-md border border-cyan-500/20 bg-cyan-500/10 p-3 text-xs text-cyan-100">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Preview is database-only. Apply contacts ProjectX for historical candles, never places orders, and leaves scheduled maintenance periods empty.
            </span>
          </div>
          <Button
            variant="outline"
            onClick={handlePreviewBrokerGapRepair}
            disabled={previewingBrokerGapRepair || applyingBrokerGapRepair}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${previewingBrokerGapRepair ? 'animate-spin' : ''}`} />
            {previewingBrokerGapRepair ? 'Checking coverage...' : 'Preview Broker Repair'}
          </Button>

          {brokerGapRepairPreview ? (
            <div className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <div><div className="text-xs text-zinc-500">Requested</div><div className="text-xl font-semibold">{brokerGapRepairPreview.requested_intervals}</div></div>
                <div><div className="text-xs text-zinc-500">Open-session</div><div className="text-xl font-semibold">{brokerGapRepairPreview.scheduled_open_intervals}</div></div>
                <div><div className="text-xs text-zinc-500">Closed-session</div><div className="text-xl font-semibold text-zinc-400">{brokerGapRepairPreview.scheduled_closed_intervals}</div></div>
                <div><div className="text-xs text-zinc-500">Existing</div><div className="text-xl font-semibold text-emerald-300">{brokerGapRepairPreview.existing_intervals_any_provider}</div></div>
                <div><div className="text-xs text-zinc-500">ProjectX existing</div><div className="text-xl font-semibold text-cyan-300">{brokerGapRepairPreview.existing_intervals_topstepx}</div></div>
                <div><div className="text-xs text-zinc-500">Missing</div><div className="text-xl font-semibold text-amber-300">{brokerGapRepairPreview.missing_scheduled_intervals}</div></div>
              </div>
              <div className="text-xs text-zinc-400">
                UTC window: {brokerGapRepairPreview.start_time} to {brokerGapRepairPreview.end_time}
              </div>
              {brokerGapRepairPreview.will_contact_broker ? (
                <div className="flex flex-col gap-2 md:flex-row">
                  <Input
                    value={brokerGapRepairConfirmation}
                    onChange={(event) => setBrokerGapRepairConfirmation(event.target.value)}
                    placeholder={brokerGapRepairPreview.confirmation_phrase}
                    className="bg-zinc-800 border-zinc-700 font-mono"
                  />
                  <Button
                    onClick={handleApplyBrokerGapRepair}
                    disabled={
                      applyingBrokerGapRepair
                      || brokerGapRepairConfirmation.trim() !== brokerGapRepairPreview.confirmation_phrase
                    }
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${applyingBrokerGapRepair ? 'animate-spin' : ''}`} />
                    {applyingBrokerGapRepair ? 'Queueing...' : 'Apply Broker Repair'}
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-emerald-300">No scheduled one-minute bars are missing in this range.</div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-300" />
                Dead-Letter Cleanup
              </CardTitle>
              <CardDescription>
                Admin-only cleanup for old inactive phantom cancel jobs. Preview is required before apply.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviewDeadLetterCleanup}
              disabled={deadLetterCleanupPreviewing || deadLetterCleanupApplying}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${deadLetterCleanupPreviewing ? 'animate-spin' : ''}`} />
              Preview
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-3">
            <div>
              <label className="text-xs text-zinc-500">Older than hours</label>
              <Input
                type="number"
                min="1"
                value={deadLetterCleanupOlderThanHours}
                onChange={(event) => {
                  setDeadLetterCleanupOlderThanHours(event.target.value);
                  setDeadLetterCleanupConfirmation('');
                }}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <label className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950/50 px-3 py-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={Boolean(deadLetterCleanupIncludeClose)}
                onChange={(event) => {
                  setDeadLetterCleanupIncludeClose(event.target.checked);
                  setDeadLetterCleanupConfirmation('');
                }}
                className="h-4 w-4 accent-blue-600"
              />
              Include old redundant close dead letters only when broker-flat is confirmed and the trade is terminal.
            </label>
          </div>

          {deadLetterCleanupPreview ? (
            <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="text-xs text-zinc-500">Phantom cancel</div>
                  <div className="text-xl font-semibold text-red-300">{cleanupCounts.phantom_cancel || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Broker-flat close</div>
                  <div className="text-xl font-semibold text-orange-300">{cleanupCounts.redundant_close_broker_flat || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Total eligible</div>
                  <div className="text-xl font-semibold text-white">{cleanupTotal}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-zinc-400">
                <span>Too recent: {cleanupExclusions.too_recent || 0}</span>
                <span>Active trade: {cleanupExclusions.linked_trade_active_open || 0}</span>
                <span>Active broker position: {cleanupExclusions.linked_trade_active_broker_position || 0}</span>
                <span>Wrong cancel error: {cleanupExclusions.cancel_wrong_error_pattern || 0}</span>
              </div>
              {cleanupTotal > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 p-2 text-xs text-amber-100">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>Type <span className="font-mono text-white">{deadLetterCleanupConfirmationPhrase}</span> to delete only the previewed safe categories.</span>
                  </div>
                  <div className="flex flex-col gap-2 md:flex-row">
                    <Input
                      value={deadLetterCleanupConfirmation}
                      onChange={(event) => setDeadLetterCleanupConfirmation(event.target.value)}
                      placeholder={deadLetterCleanupConfirmationPhrase}
                      className="bg-zinc-800 border-zinc-700 font-mono"
                    />
                    <Button
                      variant="destructive"
                      onClick={handleApplyDeadLetterCleanup}
                      disabled={!canApplyCleanup}
                    >
                      <Trash2 className={`w-4 h-4 mr-2 ${deadLetterCleanupApplying ? 'animate-pulse' : ''}`} />
                      Apply Cleanup
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-500">No eligible inactive dead-letter jobs matched the current filters.</div>
              )}
            </div>
          ) : (
            <div className="text-sm text-zinc-500">Run Preview to see eligible categories before deletion.</div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>Recent Execution Jobs</CardTitle>
          <CardDescription>Latest broker execution activity across all users, with retry controls for failed jobs.</CardDescription>
        </CardHeader>
        <CardContent>
          {executionJobs.length === 0 ? (
            <div className="text-sm text-zinc-500">No execution jobs yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead>Job</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Trade</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {executionJobs.map((job) => {
                  const canRetry = job.job_type !== 'submit' && (job.status === 'failed' || job.status === 'dead_letter');
                  return (
                    <TableRow key={job.id} className="border-zinc-800">
                      <TableCell>
                        <div>
                          <div className="font-medium text-white">
                            {job.job_type} {job.broker_name ? `• ${job.broker_name}` : ''}
                          </div>
                          <div className="text-xs text-zinc-500">{job.id.slice(0, 8)}</div>
                          {job.last_error ? (
                            <div className="mt-1 text-xs text-red-300">{job.last_error}</div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={job.status === 'dead_letter' || job.status === 'failed' ? 'destructive' : 'outline'}
                          className={job.status === 'retrying' ? 'border-yellow-500/40 text-yellow-300' : ''}
                        >
                          {job.execution_stage || job.status}
                        </Badge>
                        {job.retry_restriction && ['failed', 'dead_letter'].includes(job.status) && <div className="mt-1 text-xs text-amber-200">{job.retry_restriction}</div>}
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        {job.attempts}/{job.max_attempts}
                      </TableCell>
                      <TableCell className="text-zinc-500">{job.user_id?.slice(0, 8) || 'n/a'}</TableCell>
                      <TableCell className="text-zinc-500">{job.trade_id?.slice(0, 8) || 'n/a'}</TableCell>
                      <TableCell className="text-zinc-500 text-sm">
                        {job.created_at ? formatDateTimeDisplay(job.created_at, appTimeZone) : 'n/a'}
                      </TableCell>
                      <TableCell className="text-right">
                        {canRetry ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={retryingExecutionJobId === job.id}
                            onClick={() => handleRetryExecutionJob(job.id)}
                          >
                            <RefreshCw className={`w-4 h-4 mr-2 ${retryingExecutionJobId === job.id ? 'animate-spin' : ''}`} />
                            Retry
                          </Button>
                        ) : (
                          <span className="text-xs text-zinc-600">No action</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Historical Data Refresh</CardTitle>
                <CardDescription>
                  Queue Dukascopy CSV refresh jobs (UTC), then auto-rebuild chart aggregates.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500">Provider</label>
                <Select
                  value={marketRefreshForm.provider}
                  onValueChange={(value) => setMarketRefreshForm((current) => ({ ...current, provider: value }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="dukascopy">dukascopy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-zinc-500">Mode</label>
                <Select
                  value={marketRefreshForm.mode}
                  onValueChange={(value) => setMarketRefreshForm((current) => ({ ...current, mode: value }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="incremental">incremental</SelectItem>
                    <SelectItem value="full">full</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500">Symbol</label>
                <Select
                  value={marketRefreshForm.symbol}
                  onValueChange={handleMarketRefreshSymbolChange}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {marketDataSymbolOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-zinc-500">Timezone</label>
                <Input
                  value={marketRefreshForm.source_timezone}
                  onChange={(e) => setMarketRefreshForm((current) => ({ ...current, source_timezone: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="UTC"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500">CSV Path (server)</label>
              <div className="flex gap-2">
                <Input
                  value={marketRefreshForm.csv_path}
                  onChange={(e) => setMarketRefreshForm((current) => ({ ...current, csv_path: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="/data/raw/dukascopy/BTCUSD/file.csv"
                />
                <Button type="button" variant="outline" onClick={openMarketBrowser}>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Browse
                </Button>
              </div>
              <div className="mt-2 text-xs text-zinc-500">
                Valid CSV filenames must contain `{marketRefreshForm.symbol}` somewhere in the filename.
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500">Session Type</label>
                <Input
                  value={marketRefreshForm.session_type}
                  onChange={(e) => setMarketRefreshForm((current) => ({ ...current, session_type: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="no_session"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Aggregate Timeframes (comma-separated)</label>
                <Input
                  value={marketRefreshForm.timeframes}
                  onChange={(e) => setMarketRefreshForm((current) => ({ ...current, timeframes: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="2m,3m,5m,15m,30m,1h,4h,1d,1w,1mo"
                />
              </div>
            </div>
            <Button
              onClick={handleRunMarketRefresh}
              disabled={runningMarketRefresh}
              className="w-full"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${runningMarketRefresh ? 'animate-spin' : ''}`} />
              {runningMarketRefresh ? 'Queueing refresh...' : 'Queue Historical Data Refresh'}
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle>Market Data Coverage</CardTitle>
            <CardDescription>UTC coverage currently available to chart APIs.</CardDescription>
          </CardHeader>
          <CardContent>
            {marketDataCoverage.length === 0 ? (
              <div className="text-sm text-zinc-500">No historical data loaded yet.</div>
            ) : (
              <div className="space-y-3 max-h-[360px] overflow-auto pr-1">
                {marketDataCoverage.map((entry) => (
                  <div key={`${entry.provider}-${entry.symbol}`} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-white">{entry.provider} • {entry.symbol}</div>
                      <Badge variant="outline">{entry.bar_count_1m || 0} bars (1m)</Badge>
                    </div>
                    <div className="mt-2 text-xs text-zinc-500">
                      {entry.min_ts_utc || 'n/a'} → {entry.max_ts_utc || 'n/a'}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(entry.aggregates || {}).map(([tf, count]) => (
                        <Badge key={`${entry.symbol}-${tf}`} variant="secondary" className="text-xs">
                          {tf}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>Refresh Jobs</CardTitle>
          <CardDescription>Latest historical refresh jobs submitted by admin.</CardDescription>
        </CardHeader>
        <CardContent>
          {marketDataJobs.length === 0 ? (
            <div className="text-sm text-zinc-500">No refresh jobs yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead>Job</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rows</TableHead>
                  <TableHead>Loaded</TableHead>
                  <TableHead>Skipped</TableHead>
                  <TableHead>Rejected</TableHead>
                  <TableHead>Window</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {marketDataJobs.map((job) => (
                  <TableRow key={job.id} className="border-zinc-800">
                    <TableCell>
                      <div className="font-medium text-white">
                        {job.provider} • {job.symbol}
                      </div>
                      <div className="text-xs text-zinc-500">{job.mode} • {job.id.slice(0, 8)}</div>
                      <div className="text-xs text-zinc-600 break-all">{job.csv_path}</div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={job.status === 'failed' ? 'destructive' : 'outline'}
                        className={job.status === 'running' ? 'border-blue-500/40 text-blue-300' : ''}
                      >
                        {job.status}
                      </Badge>
                      {job.error_message ? (
                        <div className="mt-1 text-xs text-red-300">{job.error_message}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-zinc-300">{job.rows_total || 0}</TableCell>
                    <TableCell className="text-green-400">{job.rows_loaded || 0}</TableCell>
                    <TableCell className="text-yellow-400">{job.rows_skipped || 0}</TableCell>
                    <TableCell className="text-red-300">{job.rows_rejected || 0}</TableCell>
                    <TableCell className="text-xs text-zinc-500">
                      <div>{job.source_start_ts_utc || 'n/a'}</div>
                      <div>{job.source_end_ts_utc || 'n/a'}</div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OperationsTab;
